/**
 * What the Home grid should do about a `?focus=<companyRowID>` parameter (P.8, bug 5722).
 *
 * The parameter is how three different journeys ask Home to point at one client: a search result, a
 * newly created client, and "Mark as Current Client". The grid flashed the row when it happened to be
 * on the page it had just loaded, and otherwise recovered only by filtering on a company name handed
 * over in `location.state` — which only the search journey supplies.
 *
 * That left the promotion journey with no recovery at all. Home's filter and page number live on the
 * store, not the component, so they outlive the navigation: a user who had filtered or paged Home
 * earlier in the session came back from the wizard to that same filtered page, the promoted client
 * was not in it, and the grid did nothing. The modal had promised a highlight, so nothing is the one
 * outcome it must not produce.
 *
 * The decision is separated from the grid because it is the part with rules in it, and because a
 * flash is a CSS animation inside a virtualised data grid — the branch taken can be tested even
 * though the animation cannot.
 */
export type RowFocusAction =
  /** Nothing to do: no parameter, still loading, or already recovered once and still not found. */
  | { kind: "idle" }
  /** The row is on the loaded page — flash it and scroll it into view. */
  | { kind: "flash"; rowID: string }
  /** Not on the page, but the journey handed over a name to filter by (search). */
  | { kind: "filterByName"; rowID: string; companyName: string }
  /** Not on the page and nothing to filter by: clear the filter and go back to page one. */
  | { kind: "resetToDefaults"; rowID: string };

export type RowFocusInput = {
  /** The value of the `focus` query parameter, if any. */
  focusRowID: string | null | undefined;
  /** True while a fetch is in flight; the loaded page is not yet meaningful. */
  loading: boolean;
  /** The companyRowID of every row on the page the grid has loaded. */
  loadedRowIDs: readonly string[] | null | undefined;
  /** A company name carried over by the journey that navigated here, if it carried one. */
  handedOverName?: string | null;
  /** Whether a recovery has already been attempted for this focus request. */
  recoveryAttempted: boolean;
};

export const decideRowFocus = ({
  focusRowID,
  loading,
  loadedRowIDs,
  handedOverName,
  recoveryAttempted,
}: RowFocusInput): RowFocusAction => {
  if (!focusRowID) return { kind: "idle" };

  // Mid-fetch the loaded page says nothing about whether the row exists, and acting on it would
  // spend the single recovery attempt on a page that was about to be replaced.
  if (loading) return { kind: "idle" };

  if (loadedRowIDs?.includes(focusRowID)) return { kind: "flash", rowID: focusRowID };

  // Recovery runs at most once. The row may genuinely not be there — a client the user cannot see,
  // or a stale link — and retrying on every fetch would refetch forever.
  if (recoveryAttempted) return { kind: "idle" };

  const name = handedOverName?.trim();
  if (name) return { kind: "filterByName", rowID: focusRowID, companyName: name };

  return { kind: "resetToDefaults", rowID: focusRowID };
};
